import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  firstSettingsPath,
  hasAnyPermission,
  hasPermission,
  type AdminFeatureId,
} from "@tih/shared";
import { useAuth } from "../../../context/AuthContext";

type SettingsTab = {
  to: string;
  label: string;
  feature?: AdminFeatureId;
  features?: readonly AdminFeatureId[];
};

const TABS: SettingsTab[] = [
  { to: "/admin/settings/general", label: "General", feature: "settings" },
  { to: "/admin/settings/contacts", label: "Contacts", feature: "settings" },
  { to: "/admin/settings/social", label: "Social", feature: "settings" },
  { to: "/admin/settings/footer", label: "Footer", feature: "settings" },
  { to: "/admin/settings/email", label: "Email", feature: "settings" },
  { to: "/admin/settings/sms", label: "Africa's Talking", feature: "sms" },
  {
    to: "/admin/settings/address-book",
    label: "Address book",
    features: ["sms", "inbox"],
  },
  { to: "/admin/settings/youtube", label: "YouTube", feature: "settings" },
  { to: "/admin/settings/users", label: "Users", feature: "users" },
];

function tabAllowed(
  user: Parameters<typeof hasPermission>[0],
  tab: SettingsTab,
): boolean {
  if (tab.features) return hasAnyPermission(user, tab.features);
  if (tab.feature) return hasPermission(user, tab.feature);
  return false;
}

export function AdminSettingsLayout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const tabs = TABS.filter((tab) => tabAllowed(user, tab));
  const home = firstSettingsPath(user);

  if (pathname === "/admin/settings" || pathname === "/admin/settings/") {
    return <Navigate to={home} replace />;
  }

  const onAllowedTab = tabs.some(
    (tab) => pathname === tab.to || pathname.startsWith(`${tab.to}/`),
  );
  if (!onAllowedTab) {
    return <Navigate to={home} replace />;
  }

  return (
    <div>
      <header className="admin-page-header">
        <h1>Settings</h1>
        <p>
          Site visibility, footer contacts, outbound email, Africa&apos;s Talking SMS,
          address book sync, YouTube API, and admin accounts with feature permissions.
        </p>
      </header>

      <nav className="admin-tabs" aria-label="Settings sections">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) => (isActive ? "active" : undefined)}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
